import React, { useMemo, useRef, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    ArcElement,
    PointElement,
    Tooltip,
    Legend,
    Title,
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './AutoChart.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    ArcElement,
    PointElement,
    Tooltip,
    Legend,
    Title
);

export type HeadColumn = {
    title: string;
    property: string;
    visible?: boolean;
    mask?: number;
};

type AutoChartProps = {
    data: any[];
    head: HeadColumn[];
    xOptions: string[];
    yOptions: string[];
    useIndexAsXLabel?: boolean;
    exportFileName?: string;
};


const chartTypes = ['bar', 'line', 'pie'] as const;
type ChartType = typeof chartTypes[number];

const modes = ['frecuencia', 'suma'] as const;
type ModeType = typeof modes[number];

const AutoChart: React.FC<AutoChartProps> = ({
    data,
    head,
    xOptions,
    yOptions,
    useIndexAsXLabel = false,
    exportFileName = 'tabla_datos'
}) => {
    const flatData = useMemo(() => data.map((item) => item?.DataBeanProperties ?? item), [data]);

    const [xKey, setXKey] = useState<string>(xOptions[0]);
    const [yKeys, setYKeys] = useState<string[]>([]);
    const [chartType, setChartType] = useState<ChartType>('bar');
    const [mode, setMode] = useState<ModeType>('frecuencia');

    const chartRef = useRef<any>(null);

    const getTitle = (property: string): string =>
        head.find(h => h.property === property)?.title || property;

    const filteredXOptions = useMemo(() =>
  head.filter(h => xOptions.includes(h.property) ), [head, xOptions]);

const filteredYOptions = useMemo(() =>
  head.filter(h => yOptions.includes(h.property) ), [head, yOptions]);


    const chartData = useMemo(() => {
        const rawXLabels: string[] = [];
        flatData.forEach(item => {
            const xVal = item[xKey] ?? 'Sin valor';
            const str = String(xVal);
            if (!rawXLabels.includes(str)) rawXLabels.push(str);
        });

        const xLabelMap = useIndexAsXLabel
            ? Object.fromEntries(rawXLabels.map((label, i) => [label, `#${i + 1}`]))
            : Object.fromEntries(rawXLabels.map((label) => [label, label]));

        const labels = rawXLabels.map(label => xLabelMap[label]);
        const datasets: any[] = [];
        const tableData: Record<string, any>[] = [];

        if (chartType === 'pie' && yKeys.length === 1 && mode === 'frecuencia') {
            const yKey = yKeys[0];
            const countMap: Record<string, number> = {};
            flatData.forEach(item => {
                const yVal = item[yKey] ?? 'Sin valor';
                countMap[yVal] = (countMap[yVal] || 0) + 1;
            });

            return {
                labels: Object.keys(countMap),
                datasets: [{
                    label: getTitle(yKey),
                    data: Object.values(countMap),
                    backgroundColor: Object.keys(countMap).map(() => getRandomColor())
                }],
                table: Object.keys(countMap).map(label => ({
                    ID: '',
                    [getTitle(xKey)]: label,
                    [getTitle(yKey)]: countMap[label]
                }))
            };
        }

        yKeys.forEach(yKey => {
            const values: number[] = [];

            rawXLabels.forEach(rawLabel => {
                const filtered = flatData.filter(item => String(item[xKey] ?? 'Sin valor') === rawLabel);
                let value = 0;

                if (mode === 'frecuencia') {
                    value = filtered.filter(item => item[yKey] !== null && item[yKey] !== undefined).length;
                } else if (mode === 'suma') {
                    value = filtered.reduce((acc, item) => {
                        const val = item[yKey];
                        return acc + (typeof val === 'number' ? val : 0);
                    }, 0);
                }

                values.push(value);
            });

            datasets.push({
                label: getTitle(yKey),
                data: values,
                backgroundColor: getRandomColor(),
                borderColor: 'black',
                borderWidth: 1,
            });
        });

        rawXLabels.forEach((rawLabel, i) => {
            const row: Record<string, any> = {
                ID: `#${i + 1}`,
                [getTitle(xKey)]: rawLabel,
            };
            yKeys.forEach((yKey, j) => {
                row[getTitle(yKey)] = datasets[j]?.data[i];
            });
            tableData.push(row);
        });

        return { labels, datasets, table: tableData };
    }, [flatData, xKey, yKeys, chartType, mode, useIndexAsXLabel]);

    const ChartComponent = chartType === 'bar' ? Bar : chartType === 'line' ? Line : Pie;


    function getRandomColor(): string {
        const r = Math.floor(Math.random() * 200);
        const g = Math.floor(Math.random() * 200);
        const b = Math.floor(Math.random() * 200);
        return `rgba(${r}, ${g}, ${b}, 0.6)`;
    }

    function exportToExcel(data: Record<string, any>[], baseFileName: string) {
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Datos');

        const now = new Date();
        const timestamp = `${now.getFullYear()}${(now.getMonth() + 1)
            .toString()
            .padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now
                .getHours()
                .toString()
                .padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}`;

        const filename = `${baseFileName}_${timestamp}.xlsx`;
        XLSX.writeFile(wb, filename);
    }

    function exportChartAsImage(chartRef: React.RefObject<any>, fileName: string) {
        const chart = chartRef.current;
        if (chart) {
            const now = new Date();
            const timestamp = `${now.getFullYear()}${(now.getMonth() + 1)
                .toString()
                .padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now
                    .getHours()
                    .toString()
                    .padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}`;
            const link = document.createElement('a');
            link.download = `${fileName}_${timestamp}.png`;
            link.href = chart.toBase64Image();
            link.click();
        }
    }

    function exportToPDF(data: Record<string, any>[], baseFileName: string, chartRef: React.RefObject<any>) {
        const chart = chartRef.current;
        const doc = new jsPDF();
        const now = new Date();
        const timestamp = `${now.getFullYear()}${(now.getMonth() + 1)
            .toString()
            .padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now
                .getHours()
                .toString()
                .padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}`;
        const filename = `${baseFileName}_${timestamp}.pdf`;

        const headers = Object.keys(data[0]);
        const body = data.map(row => headers.map(h => row[h]));

        let yOffset = 10;

        if (chart) {
            const imageData = chart.toBase64Image();
            doc.addImage(imageData, 'PNG', 10, yOffset, 180, 80);
            yOffset += 90;
        }

        autoTable(doc, {
            head: [headers],
            body,
            startY: yOffset,
            margin: { horizontal: 10 },
            styles: { fontSize: 8 },
            headStyles: { fillColor: [22, 160, 133] },
        });

        doc.save(filename);
    }


    return (
        <div className="chart-container">
            <h2>Reporte</h2>

            <div className="controls">
                <div>
                    <label>Eje X:</label><br />
                    <select className='form-select' value={xKey} onChange={(e) => setXKey(e.target.value)}>
                        {filteredXOptions.map(opt => (
                            <option key={opt.property} value={opt.property}>{opt.title}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label>Series Y ({mode}):</label><br />
                    <select className='form-select'
                        multiple
                        value={yKeys}
                        onChange={(e) =>
                            setYKeys(Array.from(e.target.selectedOptions, opt => opt.value))
                        }
                        size={Math.min(filteredYOptions.length, 6)}
                    >
                        {filteredYOptions.map(opt => (
                            <option key={opt.property} value={opt.property}>{opt.title}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label>Tipo de gráfico:</label><br />
                    <select className='form-select' value={chartType} onChange={(e) => setChartType(e.target.value as ChartType)}>
                        {chartTypes.map(type => (
                            <option key={type} value={type}>{type.toUpperCase()}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label>Modo:</label><br />
                    <select className='form-select' value={mode} onChange={(e) => {
                        setMode(e.target.value as ModeType);
                        setYKeys([]);
                    }}>
                        {modes.map(m => (
                            <option key={m} value={m}>{m.toUpperCase()}</option>
                        ))}
                    </select>
                </div>
            </div>

            <ChartComponent
                ref={chartRef}
                data={{ labels: chartData.labels, datasets: chartData.datasets }}
                options={{
                    responsive: true,
                    plugins: {
                        legend: { position: 'top' },
                        title: { display: true, text: 'Visualización de datos' },
                    },
                    scales: {
                        x: {
                            ticks: {
                                autoSkip: true,
                                maxRotation: 45,
                                minRotation: 30,
                            },
                        },
                    },
                }}
            />

            {chartData.table.length > 0 && (
                <div className="table-wrapper">
                    <h3>Datos</h3>
                    <button className='btn btn-primary' onClick={() => exportToExcel(chartData.table, exportFileName)}>
                        📥  Excel
                    </button>
                    <button className='btn btn-primary' onClick={() => exportChartAsImage(chartRef, exportFileName)}>
                        📸 Imagen
                    </button>
                    <button className='btn btn-primary' onClick={() => exportToPDF(chartData.table, exportFileName, chartRef)}>
                        🧾 PDF
                    </button>

                    <table>
                        <thead>
                            <tr>
                                {Object.keys(chartData.table[0]).map((col, i) => (
                                    <th key={i}>{col}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {chartData.table.map((row, i) => (
                                <tr key={i}>
                                    {Object.values(row).map((cell, j) => (
                                        <td key={j}>{typeof cell === 'number' ? cell.toLocaleString() : cell}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
export default AutoChart;
